export interface MinsAndSeconds {
  mins: string;
  secs: string;
}

/**
 * Convert milliseconds to seconds
 * @param millis the milliseconds to convert
 * @returns the millis in seconds (rounded to nearest second)
 */
export function convertMillisToSeconds(millis: number): number {
  return Math.round(millis / 1000);
}

export function convertMinsToMinsAndSeconds(mins: number): MinsAndSeconds {
  return convertSecondsToMinsAndSeconds(mins * 60);
}

export function convertSecondsToMinsAndSeconds(
  seconds: number
): MinsAndSeconds {
  return {
    mins: String(Math.floor(seconds / 60)).padStart(2, "0"),
    secs: String(seconds % 60).padStart(2, "0"),
  };
}

export function getYYY_MM_DD_HH_mm_SSForTimestamp(timestamp: number): string {
  const d = new Date();
  d.setTime(timestamp!!);

  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0"); //January is 0!
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mn = String(d.getMinutes()).padStart(2, "0");
  const zz = String(d.getMilliseconds()).padStart(3, "0");
  return `${yyyy}-${mm}-${dd} ${hh}:${mn}:${zz}`;
}

export function getHH_mm_ForTimestamp(timestamp: number): string {
  const d = new Date();
  d.setTime(timestamp!!);
  return (
    String(d.getHours()).padStart(2, "0") +
    ":" +
    String(d.getMinutes()).padStart(2, "0")
  );
}

export function getHH_mm_ZZZ_ForTimestamp(timestamp: number): string {
  const d = new Date();
  d.setTime(timestamp!!);
  return (
    String(d.getHours()).padStart(2, "0") +
    ":" +
    String(d.getMinutes()).padStart(2, "0") +
    ":" +
    String(d.getMilliseconds()).padStart(3, "0")
  );
}

export function getCurrentTimeFormatted(inputIsoString?: string): string {
  let dateToFormat: Date;
  if (inputIsoString) {
    dateToFormat = new Date(inputIsoString);
  } else {
    dateToFormat = new Date();
  }
  const hours = dateToFormat.getHours().toString().padStart(2, "0");
  const minutes = dateToFormat.getMinutes().toString().padStart(2, "0");
  return `${hours}:${minutes}`;
}
export function timeToMinutes(timeString: string) {
  const [hours, minutes] = timeString.split(":").map(Number);
  return hours * 60 + minutes;
}

export function toLocalISOString(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hour = pad(date.getHours());
  const minute = pad(date.getMinutes());
  const second = pad(date.getSeconds());
  const ms = "0000000"; // mimic .NET-style 7-digit fraction
  return `${year}-${month}-${day}T${hour}:${minute}:${second}.${ms}`;
}

/**
 * Format time input to ensure colon is only at 3rd position
 * @param input The time input string to be formatted
 * @returns Formatted time string in HH:MM format
 */
export function formatTimeInput(input: string): string {
  // Remove any non-digit, non-colon characters
  let cleaned = input.replace(/[^0-9:]/g, "");

  // Find all colon positions
  const colonPositions = [];
  for (let i = 0; i < cleaned.length; i++) {
    if (cleaned[i] === ':') {
      colonPositions.push(i);
    }
  }

  // If there are colons but none at position 2, remove all colons
  if (colonPositions.length > 0 && !colonPositions.includes(2)) {
    cleaned = cleaned.replace(/:/g, '');
  }
  // If there are multiple colons or there's a colon at position other than 2,
  // keep only the one at position 2 (if it exists), remove others
  else if (colonPositions.length > 1 || colonPositions.some(pos => pos !== 2)) {
    if (colonPositions.includes(2)) {
      // Colon exists at position 2, remove other colons
      let result = '';
      for (let i = 0; i < cleaned.length; i++) {
        if (i === 2 || cleaned[i] !== ':') {
          result += cleaned[i];
        }
      }
      cleaned = result;
    } else {
      // No colon at position 2, remove all colons
      cleaned = cleaned.replace(/:/g, '');
    }
  }

  // If we have at least 3 digits and no colon, insert colon after first 2 digits
  if (cleaned.length >= 3 && !cleaned.includes(':')) {
    cleaned = cleaned.slice(0, 2) + ':' + cleaned.slice(2);
  }

  // Ensure we don't exceed 5 characters (HH:MM format)
  if (cleaned.length > 5) {
    cleaned = cleaned.substring(0, 5);
  }

  return cleaned;
}

